import React, { useState, useEffect } from "react";
import { Search, Filter, MessageCircle, Eye, Phone } from "lucide-react";
import { companyInfo } from "../data/mockData";
import { useVehicles } from "../hooks/useVehicles";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";

const InventoryPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedFuel, setSelectedFuel] = useState("");
  
  // Build filters for API call
  const filters = {
    ...(searchTerm && { search: searchTerm }),
    ...(selectedMake && { make: selectedMake }),
    ...(selectedYear && { year: parseInt(selectedYear) }),
    ...(selectedFuel && { fuel: selectedFuel }),
  };

  const { vehicles, loading, error, refetch } = useVehicles(filters);

  const handleWhatsAppClick = (vehicle = null) => {
    const message = vehicle 
      ? `Hi, I'm interested in the ${vehicle.make} ${vehicle.model} (${vehicle.year}) priced at ${vehicle.price}. Could you provide more details?`
      : `Hi, I'd like to inquire about your vehicle inventory.`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${encodedMessage}`, "_blank");
  };

  // Get unique values for filters from loaded vehicles
  const makes = [...new Set(vehicles.map(v => v.make))];
  const years = [...new Set(vehicles.map(v => v.year))].sort((a, b) => b - a);
  const fuels = [...new Set(vehicles.map(v => v.fuel))];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-64 bg-gradient-to-r from-black via-gray-900 to-royal-red/20 flex items-center">
        <div className="container mx-auto">
          <div className="animate-fadeInUp">
            <h1 className="text-5xl font-bold text-white mb-4">Vehicle Inventory</h1>
            <p className="text-xl text-gray-200">
              Discover our premium collection of luxury vehicles from Japan's finest auctions
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-gray-900">
        <div className="container mx-auto">
          <div className="glass-effect p-6 rounded-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by make or model"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
                />
              </div>

              {/* Make Filter */}
              <select
                value={selectedMake}
                onChange={(e) => setSelectedMake(e.target.value)}
                className="px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
              >
                <option value="">All Makes</option>
                {makes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>

              {/* Year Filter */}
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              {/* Fuel Filter */}
              <select
                value={selectedFuel}
                onChange={(e) => setSelectedFuel(e.target.value)}
                className="px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-royal-red outline-none"
              >
                <option value="">All Fuel Types</option>
                {fuels.map(fuel => (
                  <option key={fuel} value={fuel}>{fuel}</option>
                ))}
              </select>

              {/* Reset Button */}
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedMake("");
                  setSelectedYear("");
                  setSelectedFuel("");
                }}
                className="btn-secondary justify-center"
              >
                <Filter className="w-5 h-5" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Inventory Grid */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          {/* Results Count */}
          <div className="mb-8">
            <p className="text-gray-300">
              {loading ? "Loading vehicles..." : `Showing ${vehicles.length} vehicles`}
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {error && (
            <ErrorMessage 
              message={error} 
              onRetry={refetch}
              className="max-w-md mx-auto"
            />
          )}

          {!loading && !error && vehicles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No vehicles found matching your criteria.</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters or search terms.</p>
            </div>
          ) : !loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="glass-effect rounded-xl overflow-hidden hover-lift group">
                  <div className="relative overflow-hidden">
                    <img
                      src={vehicle.image}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        vehicle.status === 'Available' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'
                      }`}>
                        {vehicle.status}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <button className="bg-black/50 p-2 rounded-full hover:bg-black/70 transition-colors">
                        <Eye className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-white">
                        {vehicle.make} {vehicle.model}
                      </h3>
                      <span className="text-2xl font-bold text-royal-red">{vehicle.price}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-300 mb-4">
                      <div className="flex justify-between">
                        <span>Year:</span>
                        <span className="text-white">{vehicle.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Mileage:</span>
                        <span className="text-white">{vehicle.mileage}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fuel:</span>
                        <span className="text-white">{vehicle.fuel}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Transmission:</span>
                        <span className="text-white">{vehicle.transmission}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Color:</span>
                        <span className="text-white">{vehicle.color}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Engine:</span>
                        <span className="text-white">{vehicle.engine}</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-400">Location: {vehicle.location}</p>
                    </div>

                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleWhatsAppClick(vehicle)}
                        className="btn-primary flex-1 text-sm justify-center"
                      >
                        <MessageCircle className="w-4 h-4" />
                        WhatsApp
                      </button>
                      <button className="btn-secondary text-sm px-4">
                        <Phone className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-royal-red">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            Our team can source specific vehicles from Japan's extensive auction network. Contact us with your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => handleWhatsAppClick()}
              className="bg-white text-royal-red hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Custom Vehicle Request
            </button>
            <button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-royal-red px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center">
              <Phone className="w-5 h-5 mr-2" />
              Call: {companyInfo.landline}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InventoryPage;